import { notifier } from "../../core/notifier/notifier";
import { PointRuleAdminApi, type PointRuleUpdateResponse } from "./point-rule-admin-api";

interface PointRuleAdminFormLike {
    getEditButton(root: EventTarget | Element | null | undefined): HTMLButtonElement | null;
    getRow(root: EventTarget | Element | null | undefined): HTMLFormElement | null;
    getSubmitButton(root: ParentNode): HTMLButtonElement | null;
    getField(root: ParentNode, name: string): HTMLElement | null;
    getFieldDisplay(root: ParentNode, field: string): HTMLElement | null;
}

const TEXT_FIELDS = [
    "rule_name_th",
    "rule_name_en",
    "rule_name_ja",
    "description_th",
    "description_en",
    "description_ja",
    "points_per_action",
    "required_count",
    "daily_point_limit",
    "display_order",
] as const;

export class PointRuleAdminHandler {
    public form: PointRuleAdminFormLike;

    public constructor(form: PointRuleAdminFormLike) {
        this.form = form;
    }

    public handleListClick(event: MouseEvent): void {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const editButton = this.form.getEditButton(target);
        if (!(editButton instanceof HTMLButtonElement)) {
            return;
        }

        event.preventDefault();

        const row = this.form.getRow(editButton);
        if (!(row instanceof HTMLFormElement)) {
            return;
        }

        row.dataset.editing = row.dataset.editing === "1" ? "0" : "1";
    }

    public async submitRow(event: SubmitEvent): Promise<void> {
        event.preventDefault();

        const row = this.form.getRow(event.target);
        if (!(row instanceof HTMLFormElement)) {
            return;
        }

        const submitButton = this.form.getSubmitButton(row);
        const formData = new FormData(row);

        submitButton?.setAttribute("aria-busy", "true");
        if (submitButton) {
            submitButton.disabled = true;
        }

        try {
            const response = await PointRuleAdminApi.update(row, formData);
            const result = (await response.json().catch(() => ({}))) as PointRuleUpdateResponse;

            if (!response.ok || result.success !== 1 || !result.item) {
                notifier.error(this.getErrorMessage(result));
                return;
            }

            this.applyRowValues(row, result.item);
            row.dataset.editing = "0";
            notifier.success(
                typeof result.message === "string" && result.message ? result.message : "Point rule updated.",
            );
        } catch (error) {
            notifier.error(error instanceof Error && error.message ? error.message : "Point rule update failed.");
        } finally {
            submitButton?.removeAttribute("aria-busy");
            if (submitButton) {
                submitButton.disabled = false;
            }
        }
    }

    private getErrorMessage(result: PointRuleUpdateResponse): string {
        if (typeof result.message === "string" && result.message) {
            return result.message;
        }

        if (result.errors && typeof result.errors === "object") {
            const firstError = Object.values(result.errors).flat()[0];
            if (typeof firstError === "string" && firstError) {
                return firstError;
            }
        }

        return "Point rule update failed.";
    }

    private applyRowValues(row: HTMLFormElement, item: NonNullable<PointRuleUpdateResponse["item"]>): void {
        const isActive = Number(item.is_active ?? 1) === 1;

        for (const field of TEXT_FIELDS) {
            this.setFieldValue(row, field, item[field] as string | number | null | undefined);
        }
        this.setFieldValue(row, "is_active", isActive ? "1" : "0");

        const notifyField = this.form.getField(row, "notification_send_type");
        if (notifyField instanceof HTMLInputElement) {
            notifyField.checked = item.notification_send_type === "on_earn";
        }

        row.classList.toggle("users-admin__row--muted", !isActive);

        const pointsDisplay = this.form.getFieldDisplay(row, "points_per_action");
        if (pointsDisplay) {
            pointsDisplay.textContent = `+${item.points_per_action ?? 0} pt`;
            const hasPoints = Number(item.points_per_action ?? 0) > 0;
            pointsDisplay.classList.toggle("badge-config--success", hasPoints);
            pointsDisplay.classList.toggle("badge-config--muted", !hasPoints);
        }

        const countDisplay = this.form.getFieldDisplay(row, "required_count");
        if (countDisplay) {
            countDisplay.textContent = `x${item.required_count ?? 1}`;
        }

        const limitDisplay = this.form.getFieldDisplay(row, "daily_point_limit");
        if (limitDisplay instanceof HTMLElement) {
            const hasLimit = item.daily_point_limit !== null && item.daily_point_limit !== undefined && item.daily_point_limit !== "";
            limitDisplay.hidden = !hasLimit;
            limitDisplay.textContent = `max ${item.daily_point_limit ?? 0}/day`;
        }

        const statusDisplay = this.form.getFieldDisplay(row, "is_active");
        if (statusDisplay) {
            statusDisplay.textContent = isActive ? "Active" : "Inactive";
            statusDisplay.classList.remove("active", "inactive");
            statusDisplay.classList.add(isActive ? "active" : "inactive");
        }
    }

    private setFieldValue(row: HTMLFormElement, name: string, value: string | number | null | undefined): void {
        const field = this.form.getField(row, name);
        const normalized = value === null || value === undefined ? "" : String(value);

        if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
            field.value = normalized;
        }
    }
}
